// src/pages/gene.codes/GeneticWheel/ChromosomeMap/ChromosomeMap.tsx
import React, { useEffect, useRef, useState } from "react";
import { Alert } from "antd";
import Ideogram from "ideogram";
import { InlineLoading } from "../../../../components/shared";
import "./ChromosomeMap.css";

interface ChromosomeMapProps {
	geneName: string | null;
	chromosomeNumber: string | null;
	geneStart: string | null;
	geneEnd: string | null;
	height?: number;
	width?: number;
	orientation?: "horizontal" | "vertical";
	className?: string;
}

// Valid human chromosome numbers
const VALID_CHROMOSOMES = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "X", "Y"];

const ChromosomeMap: React.FC<ChromosomeMapProps> = ({ geneName, chromosomeNumber, geneStart, geneEnd, height = 400, width = 16, orientation = "horizontal", className = "" }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		let ideogramInstance: any = null;

		const initializeIdeogram = async () => {
			setLoading(true);
			setError(null);

			if (!chromosomeNumber || !geneName || !geneStart || !geneEnd) {
				setLoading(false);
				setError("Missing required chromosome or gene data");
				return;
			}

			try {
				// Normalize chromosome number (remove "chr" prefix if present)
				let chrNumber = chromosomeNumber.toString().replace(/^chr/i, "").trim();

				// Validate chromosome number format
				if (!VALID_CHROMOSOMES.includes(chrNumber)) {
					throw new Error(`Invalid chromosome number: ${chrNumber}. Must be one of: ${VALID_CHROMOSOMES.join(", ")}`);
				}

				// Parse gene coordinates
				const geneStartNum = parseInt(geneStart);
				const geneEndNum = parseInt(geneEnd);

				if (isNaN(geneStartNum) || isNaN(geneEndNum)) {
					throw new Error("Gene start or end positions are not valid numbers");
				}

				// Ensure start is less than end
				if (geneStartNum >= geneEndNum) {
					throw new Error("Gene start position must be less than end position");
				}

				// Generate a unique container ID
				const containerId = `chromosome-map-${Math.random().toString(36).substring(2, 11)}`;
				if (containerRef.current) {
					containerRef.current.id = containerId;
				}

				const config = {
					container: `#${containerId}`,
					organism: "human",
					chrHeight: height,
					chrWidth: width,
					chromosomes: [chrNumber],
					orientation: orientation,
					annotations: [
						{
							name: geneName,
							chr: chrNumber,
							start: geneStartNum,
							stop: geneEndNum,
						},
					],
					annotationHeight: 5,
					annotationsLayout: "overlay" as "overlay",
					showBandLabels: true,
					onDrawAnnots: () => {
						setLoading(false);
					},
					onLoad: () => {
						setLoading(false);
					},
					onError: (error: Error) => {
						console.error("Ideogram error:", error);
						setError(`Error loading chromosome map: ${error.message}`);
						setLoading(false);
					},
				};

				// Initialize the ideogram
				ideogramInstance = new Ideogram(config);
			} catch (err) {
				console.error("Error initializing Ideogram:", err);
				setError(`Failed to initialize chromosome map: ${err instanceof Error ? err.message : String(err)}`);
				setLoading(false);
			}
		};

		if (containerRef.current) {
			// Clear previous content
			while (containerRef.current.firstChild) {
				containerRef.current.removeChild(containerRef.current.firstChild);
			}

			initializeIdeogram();
		}

		// Cleanup function to destroy the ideogram instance
		return () => {
			if (ideogramInstance && typeof ideogramInstance.destroy === "function") {
				ideogramInstance.destroy();
			}
		};
	}, [chromosomeNumber, geneName, geneStart, geneEnd, height, width, orientation]);

	return (
		<div className={`chromosome-map-container ${className}`}>
			{loading && <InlineLoading minHeight={64} message="Loading chromosome map…" />}

			{error && <Alert message={error} type="error" showIcon className="chromosome-map__alert" />}

			<div ref={containerRef} className={`chromosome-map${loading ? " chromosome-map--pending" : ""}`} />
		</div>
	);
};

export default ChromosomeMap;
